import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import moment from "moment";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RestoreIcon from "@mui/icons-material/Restore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { DataGrid } from "@mui/x-data-grid";
import VatPreFinanceGroupDetailDialog from "./VatPreFinanceGroupDetailDialog";
import {
  buildVatCreateRequest,
  calculateSelectedVatSummary,
  formatPreFinanceMoney,
  formatPreFinancePeriod,
} from "../utils/preFinancePreviewUtils";

import { getPreFinanceYearOptions } from "../utils/preFinanceYearOptions";

const availableYears = getPreFinanceYearOptions();
const monthOptions = Array.from({ length: 12 }, (_, index) => ({
  value: index + 1,
  label: moment().month(index).format("MMMM"),
}));
const refreshPreviewErrorCodes = new Set([
  "StalePreview",
  "AlreadyPrefinanced",
  "DossierIdentityConflict",
  "PreFinanceGateBlocked",
]);

const CalculationValue = ({ value, tooltip, fontWeight, summary = false }) => (
  <Box display="flex" alignItems="center" gap={0.5}>
    <Typography variant={summary ? "h6" : "body1"} color="inherit" fontWeight={fontWeight}>
      {value}
    </Typography>
    <Tooltip title={tooltip} arrow placement="top">
      <InfoOutlinedIcon
        aria-label="Calculation information"
        tabIndex={0}
        sx={{ fontSize: 15, cursor: "help" }}
      />
    </Tooltip>
  </Box>
);

const SummaryItem = ({ label, value, tooltip }) => (
  <Box>
    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.75)" }}>
      {label}
    </Typography>
    {tooltip ? (
      <Box color="common.white">
        <CalculationValue value={value} tooltip={tooltip} summary />
      </Box>
    ) : (
      <Typography variant="h6" color="common.white">
        {value}
      </Typography>
    )}
  </Box>
);

const EmptyResults = ({ message }) => (
  <Box display="flex" alignItems="center" justifyContent="center" height="100%" p={3}>
    <Typography color="text.secondary">{message}</Typography>
  </Box>
);

const GRID_ROW_HEIGHT = 52;
const GRID_CHROME_HEIGHT = 143;
const PAGE_SIZE_STEP = 5;
const MIN_PAGE_SIZE = 5;
const MAX_PAGE_SIZE = 100;

const measureAvailableRowArea = (container) => {
  const containerHeight = container.getBoundingClientRect().height;
  if (containerHeight < 120) {
    return 0;
  }

  const gridRoot = container.querySelector(".MuiDataGrid-root");
  if (!gridRoot) {
    return Math.max(0, containerHeight - GRID_CHROME_HEIGHT);
  }

  const toolbar = gridRoot.querySelector(".MuiDataGrid-toolbarContainer");
  const columnHeaders = gridRoot.querySelector(".MuiDataGrid-columnHeaders");
  const footer = gridRoot.querySelector(".MuiDataGrid-footerContainer");
  const chromeHeight =
    (toolbar?.offsetHeight || 0) + (columnHeaders?.offsetHeight || 0) + (footer?.offsetHeight || 0);
  const gridHeight = gridRoot.getBoundingClientRect().height || containerHeight;

  return Math.max(0, gridHeight - chromeHeight);
};

const measureRowHeight = (gridRoot) => {
  if (!gridRoot) {
    return GRID_ROW_HEIGHT;
  }

  const renderedRow = gridRoot.querySelector(".MuiDataGrid-row");
  if (renderedRow?.offsetHeight > 0) {
    return renderedRow.offsetHeight;
  }

  const cssRowHeight = window.getComputedStyle(gridRoot).getPropertyValue("--DataGrid-rowHeight");
  const parsedRowHeight = Number.parseFloat(cssRowHeight);
  return Number.isFinite(parsedRowHeight) && parsedRowHeight > 0 ? parsedRowHeight : GRID_ROW_HEIGHT;
};

const calculatePageSizeForHeight = (rowAreaHeight, rowHeight = GRID_ROW_HEIGHT) => {
  if (rowAreaHeight <= 0 || rowHeight <= 0) {
    return MIN_PAGE_SIZE;
  }

  const rawCount = Math.floor(rowAreaHeight / rowHeight);
  const steppedCount = Math.floor(rawCount / PAGE_SIZE_STEP) * PAGE_SIZE_STEP;
  return Math.max(MIN_PAGE_SIZE, Math.min(MAX_PAGE_SIZE, steppedCount));
};

const buildRowsPerPageOptions = (optimalPageSize) => {
  const upperBound = Math.min(
    MAX_PAGE_SIZE,
    Math.max(optimalPageSize + PAGE_SIZE_STEP * 4, PAGE_SIZE_STEP * 6)
  );
  const options = new Set([optimalPageSize]);

  for (let size = PAGE_SIZE_STEP; size <= upperBound; size += PAGE_SIZE_STEP) {
    options.add(size);
  }

  return [...options].sort((a, b) => a - b);
};

const VatPreFinancePrepareDialog = ({
  open,
  customerId,
  countries,
  onClose,
  onCreated,
}) => {
  const [countryId, setCountryId] = useState(0);
  const [year, setYear] = useState(new Date().getFullYear());
  const [monthFrom, setMonthFrom] = useState(1);
  const [monthTo, setMonthTo] = useState(12);
  const [preview, setPreview] = useState(null);
  const [excludedSourceIds, setExcludedSourceIds] = useState([]);
  const [selectedGroupKeys, setSelectedGroupKeys] = useState([]);
  const [groupOptions, setGroupOptions] = useState({});
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState("");
  const [detailGroupKey, setDetailGroupKey] = useState(null);
  const [detailGroupSnapshot, setDetailGroupSnapshot] = useState(null);
  const [isPreviewStale, setIsPreviewStale] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [rowsPerPageOptions, setRowsPerPageOptions] = useState([5, 10, 15, 20]);
  const resultsGridRef = useRef(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    setCountryId(0);
    setYear(new Date().getFullYear());
    setMonthFrom(1);
    setMonthTo(12);
    setPreview(null);
    setExcludedSourceIds([]);
    setSelectedGroupKeys([]);
    setGroupOptions({});
    setError("");
    setDetailGroupKey(null);
    setDetailGroupSnapshot(null);
    setIsPreviewStale(false);
  }, [open]);

  const syncResultsGridPageSize = useCallback(() => {
    const container = resultsGridRef.current;
    if (!container || !open) {
      return;
    }

    const gridRoot = container.querySelector(".MuiDataGrid-root");
    const rowAreaHeight = measureAvailableRowArea(container);
    const rowHeight = measureRowHeight(gridRoot);
    const optimalPageSize = calculatePageSizeForHeight(rowAreaHeight, rowHeight);

    setPageSize((currentPageSize) => (currentPageSize === optimalPageSize ? currentPageSize : optimalPageSize));
    setRowsPerPageOptions(buildRowsPerPageOptions(optimalPageSize));
  }, [open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    let frameId = 0;

    const scheduleSync = () => {
      cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => {
        syncResultsGridPageSize();
        frameId = requestAnimationFrame(syncResultsGridPageSize);
      });
    };

    scheduleSync();

    const container = resultsGridRef.current;
    const observer = container ? new ResizeObserver(scheduleSync) : null;
    if (container && observer) {
      observer.observe(container);
    }

    window.addEventListener("resize", scheduleSync);

    return () => {
      cancelAnimationFrame(frameId);
      observer?.disconnect();
      window.removeEventListener("resize", scheduleSync);
    };
  }, [open, isPreviewLoading, preview, syncResultsGridPageSize]);

  const handleRequestPreview = async (nextExcludedSourceIds = [], preserveSelection = false) => {
    setIsPreviewLoading(true);
    setError("");

    try {
      const response = await axios.post("/api/v1/preFinance/preview", {
        entityId: Number(customerId),
        preFinanceType: 0,
        countryId: countryId || null,
        year: Number(year),
        monthFrom: Number(monthFrom),
        monthTo: Number(monthTo),
        excludedSourceIds: nextExcludedSourceIds,
      });
      const nextPreview = response.data;
      const selectableKeys = nextPreview.groups
        .filter((group) => group.isSelectable)
        .map((group) => group.groupKey);

      setPreview(nextPreview);
      setIsPreviewStale(false);
      setDetailGroupSnapshot((currentGroup) => {
        if (!currentGroup) {
          return null;
        }

        const refreshedGroup = nextPreview.groups.find(
          (group) => group.groupKey === currentGroup.groupKey
        );
        if (!refreshedGroup) {
          return currentGroup;
        }

        const sourcesById = new Map(
          [...currentGroup.sources, ...refreshedGroup.sources].map((source) => [
            source.sourceId,
            source,
          ])
        );
        return {
          ...currentGroup,
          ...refreshedGroup,
          sources: Array.from(sourcesById.values()),
        };
      });
      setExcludedSourceIds(nextExcludedSourceIds);
      setSelectedGroupKeys((currentKeys) => {
        if (!preserveSelection) {
          return selectableKeys;
        }

        const currentKeySet = new Set(currentKeys);
        return selectableKeys.filter((key) => currentKeySet.has(key));
      });
      setGroupOptions((currentOptions) =>
        nextPreview.groups.reduce((options, group) => {
          const currentOption = currentOptions[group.groupKey];

          options[group.groupKey] = {
            continueExistingPeriod:
              group.requiresExistingPeriodConfirmation &&
              Boolean(currentOption?.continueExistingPeriod),
          };
          return options;
        }, {})
      );
    } catch (requestError) {
      console.error("Unable to preview VAT prefinance:", requestError);
      setError(requestError.response?.data?.message || "The VAT prefinance preview could not be loaded.");
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handleSelectionChange = () => {
    setIsPreviewStale(Boolean(preview));
    setError("");
    setDetailGroupKey(null);
    setDetailGroupSnapshot(null);
  };

  const handleYearChange = (event) => {
    setYear(event.target.value);
    handleSelectionChange();
  };

  const handleCountryChange = (event) => {
    setCountryId(Number(event.target.value));
    handleSelectionChange();
  };

  const handleMonthFromChange = (event) => {
    const nextMonth = Number(event.target.value);
    setMonthFrom(nextMonth);
    if (nextMonth > monthTo) {
      setMonthTo(nextMonth);
    }
    handleSelectionChange();
  };

  const handleMonthToChange = (event) => {
    const nextMonth = Number(event.target.value);
    setMonthTo(nextMonth);
    if (nextMonth < monthFrom) {
      setMonthFrom(nextMonth);
    }
    handleSelectionChange();
  };

  const handleGroupSelection = (groupKey) => {
    setSelectedGroupKeys((currentKeys) =>
      currentKeys.includes(groupKey)
        ? currentKeys.filter((key) => key !== groupKey)
        : [...currentKeys, groupKey]
    );
  };

  const handleGroupOptionChange = (groupKey, field, value) => {
    setGroupOptions((currentOptions) => ({
      ...currentOptions,
      [groupKey]: {
        ...currentOptions[groupKey],
        [field]: value,
      },
    }));
  };

  const handleExcludeSources = async (sourceIds) => {
    const nextExcludedSourceIds = Array.from(
      new Set([...excludedSourceIds, ...sourceIds])
    ).sort((left, right) => left - right);
    await handleRequestPreview(nextExcludedSourceIds, true);
  };

  const handleRestoreSource = async (sourceId) => {
    const nextExcludedSourceIds = excludedSourceIds.filter((id) => id !== sourceId);
    await handleRequestPreview(nextExcludedSourceIds, true);
  };

  const handleRestoreSources = async (sourceIds) => {
    const sourceIdSet = new Set(sourceIds);
    const nextExcludedSourceIds = excludedSourceIds.filter((id) => !sourceIdSet.has(id));
    await handleRequestPreview(nextExcludedSourceIds, true);
  };

  const selectedSummary = useMemo(
    () => calculateSelectedVatSummary(preview?.groups || [], selectedGroupKeys),
    [preview, selectedGroupKeys]
  );
  const formatSelectedLocalAmount = (value) =>
    selectedSummary.hasMixedCurrencies ? "Per currency" : formatPreFinanceMoney(value);

  const selectedGroups = (preview?.groups || []).filter((group) =>
    selectedGroupKeys.includes(group.groupKey)
  );
  const unconfirmedExistingPeriodGroups = selectedGroups.filter(
    (group) =>
      group.requiresExistingPeriodConfirmation &&
      !groupOptions[group.groupKey]?.continueExistingPeriod
  );
  const hasUnconfirmedExistingPeriod = unconfirmedExistingPeriodGroups.length > 0;
  const canCreate =
    preview?.canPreview &&
    selectedGroups.length > 0 &&
    !hasUnconfirmedExistingPeriod &&
    !isPreviewStale &&
    !isPreviewLoading &&
    !isCreating;

  const handleCreate = async () => {
    if (!canCreate) {
      return;
    }

    setIsCreating(true);
    setError("");

    try {
      const request = buildVatCreateRequest({
        entityId: customerId,
        countryId,
        year,
        monthFrom,
        monthTo,
        excludedSourceIds,
        groups: preview.groups,
        selectedGroupKeys,
        groupOptions,
      });
      const response = await axios.post("/api/v1/preFinance/create", request);
      onCreated(response.data);
      onClose();
    } catch (requestError) {
      console.error("Unable to create VAT prefinance:", requestError);
      const errorCode = requestError.response?.data?.error;
      const errorMessage =
        requestError.response?.data?.message || "The VAT prefinance dossiers could not be created.";

      if (refreshPreviewErrorCodes.has(errorCode)) {
        await handleRequestPreview(excludedSourceIds, true);
        setError(`${errorMessage} The preview has been refreshed.`);
      } else {
        setError(errorMessage);
      }
    } finally {
      setIsCreating(false);
    }
  };

  const getCountryLabel = (countryId) => {
    const country = countries.find((item) => item.countryId === countryId);
    return country ? `${country.countryCode} - ${country.countryName}` : `Country ${countryId}`;
  };

  const resultRows = preview?.canPreview ? preview.groups : [];
  const detailGroup = detailGroupKey ? detailGroupSnapshot : null;
  const handleOpenGroupDetails = (group) => {
    setDetailGroupKey(group.groupKey);
    setDetailGroupSnapshot(group);
  };
  const resultColumns = [
    {
      field: "selected",
      headerName: "Exclude",
      width: 85,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Checkbox
          checked={!selectedGroupKeys.includes(row.groupKey)}
          disabled={!row.isSelectable}
          onChange={() => handleGroupSelection(row.groupKey)}
          inputProps={{
            "aria-label": `Exclude ${row.previewPreFinanceCode || row.groupKey}`,
          }}
        />
      ),
    },
    {
      field: "countryId",
      headerName: "Country",
      minWidth: 145,
      flex: 0.8,
      valueGetter: ({ row }) => getCountryLabel(row.countryId),
    },
    {
      field: "previewPreFinanceCode",
      headerName: "Prefinance Code",
      minWidth: 205,
      flex: 1,
    },
    {
      field: "invoiceCount",
      headerName: "Invoice Count",
      width: 110,
      align: "right",
      headerAlign: "right",
      valueGetter: ({ row }) => row.sources.length,
    },
    {
      field: "confirmation",
      headerName: "Confirmation",
      width: 125,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => {
        if (!row.requiresExistingPeriodConfirmation) {
          return "—";
        }

        if (groupOptions[row.groupKey]?.continueExistingPeriod) {
          return <Chip label="Confirmed" color="success" size="small" />;
        }

        return (
          <Button
            size="small"
            color="warning"
            variant="outlined"
            startIcon={<WarningAmberIcon fontSize="small" />}
            onClick={() => handleOpenGroupDetails(row)}
          >
            Confirm
          </Button>
        );
      },
    },
    {
      field: "totalGrossAmount",
      headerName: "Total Net Amount",
      width: 145,
      align: "right",
      headerAlign: "right",
      valueFormatter: ({ value }) => formatPreFinanceMoney(value),
    },
    {
      field: "refundAmount",
      headerName: "Total VAT Amount",
      width: 145,
      align: "right",
      headerAlign: "right",
      valueFormatter: ({ value }) => formatPreFinanceMoney(value),
    },
    {
      field: "totalVatAmountEur",
      headerName: "Total VAT Amount EUR",
      width: 165,
      align: "right",
      headerAlign: "right",
      valueFormatter: ({ value }) => formatPreFinanceMoney(value),
    },
    {
      field: "payoutAmount",
      headerName: "Total Payable Amount",
      width: 165,
      align: "right",
      headerAlign: "right",
      valueFormatter: ({ value }) => formatPreFinanceMoney(value),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 85,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Tooltip title="Open invoices">
          <IconButton
            aria-label={`Open invoices for ${row.previewPreFinanceCode || row.groupKey}`}
            onClick={() => handleOpenGroupDetails(row)}
            size="small"
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <React.Fragment>
    <Dialog
      open={open}
      onClose={isCreating ? undefined : onClose}
      fullWidth
      maxWidth="xl"
      PaperProps={{
        sx: {
          height: "85vh",
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6">Create pre-finance VAT</Typography>
            <Typography variant="body2" color="text.secondary">
              Preview and create one or more server-validated dossiers
            </Typography>
          </Box>
          <IconButton aria-label="Close VAT prefinance preparation" onClick={onClose} disabled={isCreating}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          flex: "1 1 0",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          minHeight: 0,
        }}
      >
        <Box sx={{ flexShrink: 0 }}>
        <Grid container xs={12} sx={{ mb: 3 }}>
          <Grid item container xs={12} md={8} spacing={5} sx={{ pr: { md: 2 } }}>
          <Grid item xs={12} sm={5}>
            <TextField
              select
              fullWidth
              label="Country"
              value={countryId}
              onChange={handleCountryChange}
              disabled={isPreviewLoading || isCreating}
            >
              <MenuItem value={0}>All countries</MenuItem>
              {countries.map((country) => (
                <MenuItem key={country.countryId} value={country.countryId}>
                  {country.countryCode} - {country.countryName}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={5}>
            <TextField
              select
              fullWidth
              label="Year"
              value={year}
              onChange={handleYearChange}
              disabled={isPreviewLoading || isCreating}
            >
              {availableYears.map((availableYear) => (
                <MenuItem key={availableYear} value={availableYear}>
                  {availableYear}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={5}>
            <TextField
              select
              fullWidth
              label="Start month"
              value={monthFrom}
              onChange={handleMonthFromChange}
              disabled={isPreviewLoading || isCreating}
            >
              {monthOptions.map((month) => (
                <MenuItem key={month.value} value={month.value}>
                  {month.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={5}>
            <TextField
              select
              fullWidth
              label="End month"
              value={monthTo}
              onChange={handleMonthToChange}
              disabled={isPreviewLoading || isCreating}
            >
              {monthOptions.map((month) => (
                <MenuItem key={month.value} value={month.value}>
                  {month.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <Button
              variant="outlined"
              sx={{ mt: 6 }}
              onClick={() => handleRequestPreview([], false)}
              disabled={isPreviewLoading || isCreating}
            >
              {isPreviewLoading ? <CircularProgress size={24} color="inherit" /> : "Preview pre-finance VAT"}
            </Button>
          </Grid>
          </Grid>

          <Grid item xs={12} md={4} sx={{ bgcolor: "#376fd0" }}>
            <Box sx={{ p: 5, pt: 3 }}>
              <Typography variant="h6" color="common.white" gutterBottom>
                Selected summary
              </Typography>
              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                <SummaryItem label="Dossiers" value={selectedSummary.groupCount} />
                <SummaryItem label="Invoices" value={selectedSummary.sourceCount} />
                <SummaryItem
                  label="Net amount"
                  value={formatSelectedLocalAmount(selectedSummary.totalGrossAmount)}
                />
                <SummaryItem
                  label="VAT local"
                  value={formatSelectedLocalAmount(selectedSummary.totalRefundAmount)}
                />
                <SummaryItem
                  label="VAT EUR"
                  value={formatPreFinanceMoney(selectedSummary.totalVatAmountEur)}
                />
                <SummaryItem
                  label="Costs"
                  value={formatSelectedLocalAmount(selectedSummary.totalCosts)}
                  tooltip={
                    selectedSummary.hasMixedCurrencies
                      ? "Costs are calculated per dossier currency and cannot be combined."
                      : (
                        <Box>
                          <Typography variant="body2">
                            VF costs ={" "}
                            {selectedSummary.vfPercentages.length === 1
                              ? `${formatPreFinanceMoney(
                                  selectedSummary.totalRefundAmount
                                )} × ${formatPreFinanceMoney(
                                  selectedSummary.vfPercentages[0]
                                )}%`
                              : `selected amounts × ${selectedSummary.vfPercentages
                                  .map(
                                    (percentage) =>
                                      `${formatPreFinanceMoney(percentage)}%`
                                  )
                                  .join(", ")}`}{" "}
                            = {formatPreFinanceMoney(selectedSummary.totalRefundFee)}
                          </Typography>
                          {selectedSummary.totalVatOnDeclarationFee > 0 ? (
                            <Typography variant="body2">
                              NL VAT costs ={" "}
                              {formatPreFinanceMoney(selectedSummary.totalRefundFee)} × 21% ={" "}
                              {formatPreFinanceMoney(
                                selectedSummary.totalVatOnDeclarationFee
                              )}
                            </Typography>
                          ) : (
                            <Typography variant="body2">
                              NL VAT costs = not applicable = 0,00
                            </Typography>
                          )}
                          <Typography variant="body2" fontWeight={600}>
                            Total costs ={" "}
                            {formatPreFinanceMoney(selectedSummary.totalRefundFee)} +{" "}
                            {formatPreFinanceMoney(
                              selectedSummary.totalVatOnDeclarationFee
                            )} = {formatPreFinanceMoney(selectedSummary.totalCosts)}
                          </Typography>
                        </Box>
                      )
                  }
                />
                <SummaryItem
                  label="Payable"
                  value={formatSelectedLocalAmount(selectedSummary.totalPayoutAmount)}
                  tooltip={
                    selectedSummary.hasMixedCurrencies
                      ? "Payable is calculated per dossier currency and cannot be combined."
                      : (
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {formatPreFinanceMoney(selectedSummary.totalRefundAmount)} VAT Local −{" "}
                            {formatPreFinanceMoney(selectedSummary.totalRefundFee)} VF costs −{" "}
                            {formatPreFinanceMoney(
                              selectedSummary.totalVatOnDeclarationFee
                            )} NL VAT costs ={" "}
                            {formatPreFinanceMoney(selectedSummary.totalPayoutAmount)}
                          </Typography>
                        </Box>
                      )
                  }
                />
                <SummaryItem label="Missing files" value={selectedSummary.missingFileCount} />
              </Box>
              {preview?.gate?.missingDocumentTypeIds?.length > 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Missing document types: {preview.gate.missingDocumentTypeIds.join(", ")}
                </Alert>
              )}
              {selectedSummary.hasMixedCurrencies && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Local-currency amounts cannot be combined across countries. See each dossier
                  group for its payable amount.
                </Alert>
              )}
            </Box>
          </Grid>
        </Grid>
        </Box>

        <Box sx={{ flexShrink: 0 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {preview && isPreviewStale && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            The filter options have changed. Preview again to refresh the results.
          </Alert>
        )}

        {preview && !preview.canPreview && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Prefinance is not available for this entity:
            </Typography>
            {preview.gate.issues.map((issue) => (
              <Typography key={`${issue.code}-${issue.documentTypeId || "general"}`} variant="body2">
                • {issue.message}
              </Typography>
            ))}
          </Alert>
        )}

        {hasUnconfirmedExistingPeriod && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              A pre-finance dossier already exists for this exact period. Confirm that you want
              to create an additional version for:{" "}
              <strong>
                {unconfirmedExistingPeriodGroups
                  .map(
                    (group) =>
                      `${group.previewPreFinanceCode || group.groupKey} · ${getCountryLabel(
                        group.countryId
                      )} · ${formatPreFinancePeriod(group)}`
                  )
                  .join("; ")}
              </strong>
              . Use Confirm in the corresponding result row.
            </Typography>
          </Alert>
        )}
        </Box>

        <Box
          ref={resultsGridRef}
          sx={{
            flex: 1,
            minHeight: 240,
            height: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Typography variant="h6" gutterBottom sx={{ flexShrink: 0 }}>
            Results
          </Typography>
          <Box sx={{ flex: 1, minHeight: 180, height: 0, minWidth: 0 }}>
            <DataGrid
              getRowId={(row) => row.groupKey}
              rows={resultRows}
              columns={resultColumns}
              disableSelectionOnClick
              density="compact"
              pagination
              paginationMode="client"
              pageSize={pageSize}
              onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
              rowsPerPageOptions={rowsPerPageOptions}
              loading={isPreviewLoading}
              components={{
                NoRowsOverlay: () => (
                  <EmptyResults
                    message={
                      preview
                        ? "No eligible VAT invoices were found for this period."
                        : "Select the filter options and start the preview to show results."
                    }
                  />
                ),
              }}
              sx={{
                height: "100%",
                minHeight: 180,
                borderRadius: 1,
                backgroundColor: "background.paper",
                boxShadow: 2,
              }}
            />
          </Box>

          {preview?.excludedSources?.length > 0 && (
            <Accordion
              disableGutters
              sx={{
                flexShrink: 0,
                mt: 1,
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>
                  Excluded invoices ({preview.excludedSources.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  px: 1,
                  py: 0,
                  maxHeight: "min(28vh, 320px)",
                  overflowY: "auto",
                }}
              >
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small" stickyHeader aria-label="Excluded VAT invoices">
                    <TableHead>
                      <TableRow>
                        <TableCell>Invoice</TableCell>
                        <TableCell>Supplier</TableCell>
                        <TableCell>Reason</TableCell>
                        <TableCell align="right">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {preview.excludedSources.map((source) => (
                        <TableRow key={source.sourceId}>
                          <TableCell>{source.sourceCode}</TableCell>
                          <TableCell>{source.supplierName || source.supplierId || "—"}</TableCell>
                          <TableCell>{source.exclusionReason}</TableCell>
                          <TableCell align="right">
                            {source.exclusionReason === "ExcludedByUser" && (
                              <Tooltip title="Include invoice again">
                                <IconButton
                                  aria-label={`Include invoice ${source.sourceCode}`}
                                  size="small"
                                  onClick={() => handleRestoreSource(source.sourceId)}
                                  disabled={isPreviewLoading || isCreating}
                                >
                                  <RestoreIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "space-between", px: 3, py: 2 }}>
        <Button type="button" variant="contained" color="primary" onClick={onClose} disabled={isCreating}>
          Close
        </Button>
        <Button variant="contained" onClick={handleCreate} disabled={!canCreate}>
          {isCreating ? <CircularProgress size={24} color="inherit" /> : "Create pre-finance VAT"}
        </Button>
      </DialogActions>
    </Dialog>
    <VatPreFinanceGroupDetailDialog
      open={Boolean(detailGroup)}
      group={detailGroup}
      countryLabel={detailGroup ? getCountryLabel(detailGroup.countryId) : ""}
      option={detailGroup ? groupOptions[detailGroup.groupKey] : null}
      isBusy={isPreviewLoading || isCreating}
      onClose={() => {
        setDetailGroupKey(null);
        setDetailGroupSnapshot(null);
      }}
      onGroupOptionChange={handleGroupOptionChange}
      onExcludeSources={handleExcludeSources}
      onRestoreSources={handleRestoreSources}
      excludedSourceIds={excludedSourceIds}
    />
    </React.Fragment>
  );
};

export default VatPreFinancePrepareDialog;
